import { Box, Button, Paper, Typography, styled } from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import { PlanCardWrapperProps } from "../types/subscription";

export const PlanCardWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isPopular',
})<PlanCardWrapperProps>(({ theme, isPopular }) => ({
  height: '100%',
  position: 'relative',
  marginTop: isPopular && !theme.breakpoints.down('sm') ? '-16px' : '0',
  marginBottom: isPopular && !theme.breakpoints.down('sm') ? '-16px' : '0',
  '& .MuiCard-root': {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    boxShadow: 'none',
    overflow: 'visible',
    backgroundColor: '#FFFFFF',
    border: isPopular ? `2px solid ${theme.palette.primary[400]}` : '1px solid #EEEDFF',
  },
  '& .MuiCardContent-root': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(1.5),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
}));

export const PopularBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  margin: 'auto',
  backgroundColor: theme.palette.primary[400],
  color: '#FFFFFF',
  padding: theme.spacing(1),
  paddingLeft: '0.8rem',
  paddingRight: '0.8rem',
  borderBottomLeftRadius: '4px',
  borderBottomRightRadius: '4px',
  textAlign: 'center',
  fontWeight: 600,
  fontSize: '0.85rem',
  zIndex: 1,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5),
  },
  width: 'fit-content',
}));

export const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(0.5),
  },
}));

export const CheckIconStyled = styled(CheckIcon)(({ theme }) => ({
  color: theme.palette.secondary[400],
  marginRight: theme.spacing(1),
  marginTop: '3px',
  fontSize: '1.2rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    marginTop: '2px',
    marginRight: theme.spacing(0.5),
  },
}));

export const ContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  width: '100%',
  maxWidth: '1000px',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2, 2),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 1),
  },
}));

export const SavingsText = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary[400],
  fontSize: '0.875rem',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  fontWeight: 600,
  lineHeight: 1.4,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  backgroundColor: theme.palette.background.default,
  padding: '0.5rem',
  width: '9.5rem',
  borderRadius: '4px',
  border: `1px solid ${theme.palette.primary[400]}`,
  textAlign: 'center',
  margin: '0px auto',
}));

export const PlanTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary[800],
  fontWeight: 600,
  fontSize: '1.4rem',
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    marginBottom: theme.spacing(0.5),
  },
}));

export const ContentPaper = styled(Paper)(({ }) => ({
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  marginTop: 0,
}));

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: theme.palette.primary[400],
  textAlign: 'center',
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    fontSize: '1.75rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

export const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666666',
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
    marginBottom: theme.spacing(3),
  },
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  color: '#333333',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.125rem',
  },
}));

export const SecureLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.75rem',
  color: '#888888',
  margin: theme.spacing(2, 0),
}));

export const SecureText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: '#888888',
  marginLeft: theme.spacing(0.5),
  marginRight: theme.spacing(1),
}));

export const Divider = styled(Box)(({ }) => ({
  display: 'inline-block',
  color: '#888888',
  margin: '0 8px',
}));

export const SubmitButton = styled(Button)(({ theme }) => ({
  background: theme.palette.primary[500],
  color: '#FFFFFF',
  borderRadius: '4px',
  fontWeight: 600,
  padding: theme.spacing(1.5, 2),
  width: '100%',
  textTransform: 'none',
  fontSize: '1rem',
  '&:hover': {
    backgroundColor: theme.palette.primary[600],
  },
  '&:disabled': {
    backgroundColor: '#CCCCCC',
    color: '#666666',
  },
}));

export const ApplicationNote = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: '#888888',
  textAlign: 'center',
  marginTop: theme.spacing(1),
}));

export const SummaryContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#F8F9FF',
  padding: theme.spacing(3),
  height: '100%',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
}));

export const SummaryTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 700,
  marginBottom: theme.spacing(2.5),
  color: theme.palette.text.primary,
}));

export const SummaryRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1),
  },
}));

export const SummaryLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
  },
}));

export const SummaryValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '0.875rem',
  fontWeight: 500,
  textAlign: 'right',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
  },
}));

export const TotalContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
}));

export const TotalLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 700,
  fontSize: '1rem',
}));

export const TotalValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 700,
  fontSize: '1.25rem',
}));